import { useMemo } from "react";

import { useMediaQuery } from "@mui/material";
import {
    Button,
    SPACING,
    PopoverWrapper,
    usePopover,
    FONT_WEIGHTS,
    MEDIA,
    ButtonSkeleton,
    SectionedMenuItems
} from "@bridgesplit/ui";
import {
    RemoveCircle,
    ExpandMore,
    FlashOnOutlined,
    TuneOutlined,
    Visibility,
    History,
    Loop,
    ShieldOutlined
} from "@mui/icons-material";
import { AbfOrderFundingType, TokenListMetadata } from "@bridgesplit/abf-sdk";
import {
    AbfLoanExpanded,
    getZcCollateral,
    isLoopLoan,
    LoopPositionExpanded,
    isClmmMetadata,
    isWhirlpoolMetadata,
    isRaydiumMetadata,
    LoopRouteType,
    isVaultLpCollateral,
    getZcLedger,
    isLoanDefaulted,
    isLoanLiquidated
} from "@bridgesplit/abf-react";

import { LoanManagementActionCategory, ManageLoanActions, GroupedLoanActions, LoanAction } from "./types";
import { TokenImage } from "../../common";
import { ManualManageCard } from "../actions/ManualManageCard";
import { AdjustCollateral } from "../actions/AdjustCollateral";
import { AdjustDebt } from "../actions/AdjustDebt";
import { hasRolloverPtMint, useLoanActionMenuItemSections } from "../actions/util";
import { AutoManageLeverageSliderCard } from "../actions/payments";
import RefinanceLoan from "../actions/RefinanceLoan";
import RefinanceProtectionSettings from "../actions/RefinanceProtectionSettings";
import LoopUnwind from "../actions/LoopUnwind";
import RolloverPt from "../actions/RolloverPt";
import { ManageWhirlpool } from "../actions/whirlpool";
import { ManageRaydium } from "../actions/raydium";
import { useToggleLoanTypeFlow } from "../actions";

export function CardLoanActionsButton({
    selectedAction,
    onActionClick,
    loanExpanded,
    loopPosition,
    isNestedDialogView,
    loading,
    onToggleLoanTypeSuccess
}: {
    selectedAction: LoanAction | undefined;
    onActionClick: (action: LoanAction) => void;
    loanExpanded: AbfLoanExpanded | undefined;
    loopPosition: LoopPositionExpanded | undefined;
    isNestedDialogView: boolean;
    loading?: boolean;
    onToggleLoanTypeSuccess?: () => void;
}) {
    const { open: openPopover, popoverProps, close } = usePopover();
    const collateral = getZcCollateral(loanExpanded);
    const principalMetadata = loanExpanded?.principalMetadata;
    const collateralMetadata = collateral?.metadata;
    const actionGroups = useLoanActions(
        loanExpanded,
        loopPosition,
        principalMetadata,
        collateralMetadata,
        isNestedDialogView,
        onToggleLoanTypeSuccess
    );

    const sections = useLoanActionMenuItemSections(actionGroups, onActionClick);

    return loading ? (
        <ButtonSkeleton height="35px" width="100px" />
    ) : (
        <>
            <Button
                variant="outlined"
                height={35}
                onClick={openPopover}
                sx={{
                    gap: 0.5
                }}
                textProps={{
                    variant: "body1",
                    sx: {
                        fontWeight: FONT_WEIGHTS.normal
                    }
                }}
            >
                {selectedAction?.icon} {selectedAction?.buttonSelectedText} <ExpandMore fontSize="small" />
            </Button>
            <PopoverWrapper
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ horizontal: "right", vertical: -SPACING }}
                {...popoverProps}
            >
                <SectionedMenuItems items={sections} close={close} />
            </PopoverWrapper>
        </>
    );
}

export function useLoanActions(
    loanExpanded: AbfLoanExpanded | undefined,
    loopPosition: LoopPositionExpanded | undefined,
    principalMetadata: TokenListMetadata | undefined,
    collateralMetadata: TokenListMetadata | undefined,
    isNestedDialogView: boolean,
    onToggleLoanTypeSuccess?: () => void
): GroupedLoanActions {
    const isMobile = useMediaQuery(MEDIA.SM.below);
    const toggleLoanTypeFlow = useToggleLoanTypeFlow();
    const allowedActions = useLoanAllowedActions({
        loanExpanded,
        loopPosition,
        collateralMetadata,
        isNestedDialogView,
        isMobile
    });

    const actions = useMemo((): LoanAction[] => {
        return allowedActions
            .map((action): LoanAction | null => {
                switch (action) {
                    case ManageLoanActions.AutoManage:
                        return {
                            buttonDropdownText: "Auto-leverage",
                            buttonSelectedText: "Auto",
                            action,
                            component: (
                                <AutoManageLeverageSliderCard loanExpanded={loanExpanded} loopPosition={loopPosition} />
                            ),
                            icon: <FlashOnOutlined />,
                            disabled: !isLoopLoan(loanExpanded) || isClmmMetadata(collateralMetadata)
                        };
                    case ManageLoanActions.ManualManage:
                        return {
                            buttonDropdownText: "Manual leverage",
                            buttonSelectedText: "Manual",
                            action,
                            component: (
                                <ManualManageCard
                                    loanExpanded={loanExpanded}
                                    isNestedDialogView={isNestedDialogView}
                                    loopPosition={loopPosition}
                                />
                            ),
                            icon: <TuneOutlined />
                        };
                    case ManageLoanActions.AdjustDebt:
                        return {
                            buttonDropdownText: "Adjust debt",
                            buttonSelectedText: "Adjust debt",
                            component: (
                                <AdjustDebt
                                    loanExpanded={loanExpanded}
                                    loopPosition={loopPosition}
                                    isNestedDialogView={isNestedDialogView}
                                />
                            ),
                            action,
                            icon: <TokenImage metadata={principalMetadata} size="sm" />
                        };
                    case ManageLoanActions.ManageLiquidity: {
                        const isWhirlpool = isWhirlpoolMetadata(collateralMetadata);
                        const isRaydium = isRaydiumMetadata(collateralMetadata);
                        if (!isWhirlpool && !isRaydium) return null;
                        return {
                            buttonDropdownText: "Manage liquidity",
                            buttonSelectedText: "Manage liquidity",
                            dialogHeaderLabel: "Manage liquidity",
                            component: isWhirlpool ? (
                                <ManageWhirlpool loanExpanded={loanExpanded} />
                            ) : (
                                <ManageRaydium loanExpanded={loanExpanded} />
                            ),
                            action,
                            icon: <TuneOutlined />,
                            isDrawer: true
                        };
                    }
                    case ManageLoanActions.AdjustCollateral:
                        return {
                            buttonDropdownText: "Adjust collateral",
                            buttonSelectedText: "Adjust collateral",
                            dialogHeaderLabel: "Adjust collateral",
                            component: (
                                <AdjustCollateral
                                    loanExpanded={loanExpanded}
                                    loopPosition={loopPosition}
                                    isNestedDialogView={isNestedDialogView}
                                />
                            ),
                            action,
                            icon: <TokenImage metadata={collateralMetadata} size="sm" />
                        };
                    case ManageLoanActions.UpdateTerms:
                        return {
                            buttonDropdownText: "Update terms",
                            buttonSelectedText: "Update terms",
                            dialogHeaderLabel: "Update terms",
                            component: <RefinanceLoan loanExpanded={loanExpanded} loopPosition={loopPosition} />,
                            action,
                            icon: <History />
                        };
                    case ManageLoanActions.Rollover:
                        return {
                            buttonDropdownText: "Rollover",
                            buttonSelectedText: "Rollover",
                            action,
                            component: <RolloverPt loanExpanded={loanExpanded} />,
                            icon: <Loop />
                        };
                    case ManageLoanActions.RefinanceProtection:
                        return {
                            buttonDropdownText: "Refinance protection",
                            buttonSelectedText: "Protection",
                            dialogHeaderLabel: "Refinance protection",
                            component: <RefinanceProtectionSettings loanExpanded={loanExpanded} />,
                            action,
                            icon: <ShieldOutlined />
                        };
                    case ManageLoanActions.ClosePosition:
                        return {
                            buttonDropdownText: "Close position",
                            buttonSelectedText: "Close",
                            component: (
                                <LoopUnwind loopPosition={loopPosition} inNestedDialogView={isNestedDialogView} />
                            ),
                            action,
                            icon: <RemoveCircle />
                        };
                    case ManageLoanActions.ToggleLoanType:
                        return {
                            buttonDropdownText:
                                loanExpanded?.loanType === AbfOrderFundingType.Loop ? "View as Loan" : "View as Loop",
                            buttonSelectedText:
                                loanExpanded?.loanType === AbfOrderFundingType.Loop ? "View as Loan" : "View as Loop",
                            component: null,
                            action,
                            icon: <Visibility />,
                            onClick: () => toggleLoanTypeFlow(loanExpanded, { onSuccess: onToggleLoanTypeSuccess })
                        };
                    default:
                        return null;
                }
            })
            .filter((a): a is LoanAction => a !== null);
    }, [
        allowedActions,
        collateralMetadata,
        isNestedDialogView,
        loanExpanded,
        loopPosition,
        onToggleLoanTypeSuccess,
        principalMetadata,
        toggleLoanTypeFlow
    ]);

    return useMemo(() => groupLoanActions(actions), [actions]);
}

function groupLoanActions(actions: LoanAction[]): GroupedLoanActions {
    const grouped: GroupedLoanActions = {
        [LoanManagementActionCategory.Leverage]: [],
        [LoanManagementActionCategory.DebtAndCollateral]: [],
        [LoanManagementActionCategory.Miscellaneous]: []
    };

    for (const action of actions) {
        grouped[getLoanActionCategory(action.action)].push(action);
    }

    return grouped;
}

function getLoanActionCategory(action: ManageLoanActions): LoanManagementActionCategory {
    switch (action) {
        case ManageLoanActions.AutoManage:
        case ManageLoanActions.ManualManage:
            return LoanManagementActionCategory.Leverage;
        case ManageLoanActions.AdjustDebt:
        case ManageLoanActions.AdjustCollateral:
        case ManageLoanActions.ManageLiquidity:
            return LoanManagementActionCategory.DebtAndCollateral;
        case ManageLoanActions.ClosePosition:
        case ManageLoanActions.UpdateTerms:
        case ManageLoanActions.ToggleLoanType:
        case ManageLoanActions.Rollover:
        case ManageLoanActions.RefinanceProtection:
            return LoanManagementActionCategory.Miscellaneous;
        default:
            return LoanManagementActionCategory.Miscellaneous;
    }
}

function useLoanAllowedActions({
    loanExpanded,
    loopPosition,
    collateralMetadata,
    isNestedDialogView,
    isMobile
}: {
    loanExpanded: AbfLoanExpanded | undefined;
    loopPosition: LoopPositionExpanded | undefined;
    collateralMetadata: TokenListMetadata | undefined;
    isNestedDialogView: boolean;
    isMobile: boolean;
}) {
    const isLoop = isLoopLoan(loanExpanded);
    const isClmm = isClmmMetadata(collateralMetadata);
    const isWhirlpool = isWhirlpoolMetadata(collateralMetadata);
    const isRaydium = isRaydiumMetadata(collateralMetadata);
    const isRollover = hasRolloverPtMint(collateralMetadata);
    const isManualRouteType = loopPosition?.loopExpanded?.loopVault.routeType === LoopRouteType.Manual;
    const isLpVaultCollateral = isVaultLpCollateral(collateralMetadata);
    const isFullyRepaid = (getZcLedger(loanExpanded)?.ledgerDebt.total ?? 0) === 0;
    const canWithdrawRemainingCollateral =
        (loanExpanded?.collateral.some((collateral) => collateral.amount > 0) ?? false) &&
        (isLoanDefaulted(loanExpanded) || isLoanLiquidated(loanExpanded));

    return useMemo((): ManageLoanActions[] => {
        const allowed: ManageLoanActions[] = [];
        const nested = isNestedDialogView;

        if (canWithdrawRemainingCollateral) {
            return [ManageLoanActions.AdjustCollateral];
        }

        // Leverage
        const showAutoManage = !nested && !isClmm && isLoop && !isManualRouteType && !isLpVaultCollateral;
        if (showAutoManage) allowed.push(ManageLoanActions.AutoManage);

        const showManualManage = !nested && !isClmm && isLoop && !isLpVaultCollateral;
        if (showManualManage) allowed.push(ManageLoanActions.ManualManage);

        // Debt & Collateral
        const showAdjustDebt = (!nested || isLoop || isClmm) && !isFullyRepaid;
        if (showAdjustDebt) allowed.push(ManageLoanActions.AdjustDebt);

        const showManageLiquidity = (isWhirlpool || isRaydium) && !nested && isMobile;
        if (showManageLiquidity) allowed.push(ManageLoanActions.ManageLiquidity);

        const canAdjustNonClmmCollateral = !isClmm;
        if (canAdjustNonClmmCollateral) allowed.push(ManageLoanActions.AdjustCollateral);

        // Miscellaneous
        if (!isFullyRepaid) allowed.push(ManageLoanActions.UpdateTerms);

        // Borrower-set refinance liquidation-threshold floor (advanced).
        if (!isFullyRepaid) allowed.push(ManageLoanActions.RefinanceProtection);

        const showRollover = !nested && isRollover;
        if (showRollover) allowed.push(ManageLoanActions.Rollover);

        // Close stays available even for collateral without an atomic exit
        // (e.g. sUSD.tel today): the unwind quote is the authoritative
        // atomicity check — no route → clean quote error; a new DEX pool
        // lights this up with zero code change.
        const showClosePosition = isLoop && !isManualRouteType && !isLpVaultCollateral;
        if (showClosePosition) allowed.push(ManageLoanActions.ClosePosition);

        const showToggleLoanType = !isFullyRepaid && !isClmm && !nested && !isLpVaultCollateral;
        if (showToggleLoanType) allowed.push(ManageLoanActions.ToggleLoanType);

        return allowed;
    }, [
        isClmm,
        isLoop,
        isMobile,
        isNestedDialogView,
        isRaydium,
        isRollover,
        isWhirlpool,
        isManualRouteType,
        isLpVaultCollateral,
        isFullyRepaid,
        canWithdrawRemainingCollateral
    ]);
}
